import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { getMembres, createPaiement, getMontantsMensuels, getCotisationsImpayeesByMois, getCotisationsByMembre, createCotisation } from '../services/api';
import { notificationService } from '../utils/notifications';
import Footer from '../components/Footer';
import './ConfirmationPaiements.css';

const ConfirmationPaiementsFinances = () => {
  const navigate = useNavigate();
  const { membreId } = useParams();
  const finances = useMemo(() => {
    try {
      return JSON.parse(localStorage.getItem('finances_user') || '{}');
    } catch {
      return {};
    }
  }, []);

  const [membre, setMembre] = useState(null);
  const [montantsMensuels, setMontantsMensuels] = useState([]);
  const [cotisationsParMois, setCotisationsParMois] = useState({});
  const [loadingCotisationsParMois, setLoadingCotisationsParMois] = useState({});
  const [moisExpande, setMoisExpande] = useState({});
  const [loading, setLoading] = useState(true);
  const [tousLesMois, setTousLesMois] = useState([]);

  const loadData = useCallback(async () => {
    try {
      setLoading(true);
      console.log('📊 Chargement des données pour le membre:', membreId);
      
      const [membresRes, montantsRes, cotisationsRes] = await Promise.all([
        getMembres(),
        getMontantsMensuels(),
        getCotisationsByMembre(parseInt(membreId))
      ]);
      
      console.log('✅ Données chargées:', {
        membres: membresRes.data?.length || 0,
        montants: montantsRes.data?.length || 0,
        cotisations: cotisationsRes.data?.length || 0
      });
      
      // Filtrer les membres pour exclure les membres de famille
      const membres = (membresRes.data || []).filter(membre => {
        const isMembreFamille = membre.type === 'famille' || 
                                 membre.famille_id || 
                                 (membre.numero_matricule && membre.numero_matricule.startsWith('FAM-'));
        return !isMembreFamille;
      });
      
      const membreData = membres.find(m => m.id === parseInt(membreId));
      
      if (!membreData) {
        console.error('❌ Membre non trouvé:', membreId);
        notificationService.error('Erreur', 'Membre non trouvé');
        navigate('/finances');
        return;
      }
      
      setMembre(membreData);
      const montants = montantsRes.data || [];
      setMontantsMensuels(montants);
      
      // Extraire tous les mois uniques à partir des cotisations
      const cotisations = cotisationsRes.data || [];
      console.log('📋 Cotisations trouvées:', cotisations.length);
      
      const moisUniques = new Map();
      
      // D'abord, ajouter tous les mois qui ont des cotisations
      cotisations.forEach(cotisation => {
        const key = `${cotisation.annee}-${cotisation.mois}`;
        if (!moisUniques.has(key)) {
          moisUniques.set(key, {
            annee: cotisation.annee,
            mois: cotisation.mois,
            cotisations: []
          });
        }
        moisUniques.get(key).cotisations.push(cotisation);
      });
      
      // Ensuite, ajouter TOUS les mois qui ont un montant mensuel fixé, même les mois futurs
      // MAIS seulement à partir du mois/année de la date d'adhésion du membre
      if (membreData.date_adhesion) {
        const dateAdhesion = new Date(membreData.date_adhesion);
        const anneeAdhesion = dateAdhesion.getFullYear();
        const moisAdhesion = dateAdhesion.getMonth() + 1; // getMonth() retourne 0-11
        
        montants.forEach(montant => {
          // Vérifier si le mois/année est après ou égal au mois/année d'adhésion
          // Inclure tous les mois futurs avec un montant fixé, sans restriction de date actuelle
          const moisApresAdhesion = montant.annee > anneeAdhesion || 
                                    (montant.annee === anneeAdhesion && montant.mois >= moisAdhesion);
          
          if (moisApresAdhesion) {
            const key = `${montant.annee}-${montant.mois}`;
            if (!moisUniques.has(key)) {
              // Ce mois a un montant fixé mais pas encore de cotisations pour ce membre
              // Inclure même les mois futurs
              moisUniques.set(key, {
                annee: montant.annee,
                mois: montant.mois,
                cotisations: [] // Pas encore de cotisations créées
              });
            }
          }
        });
      } else {
        // Si pas de date d'adhésion, inclure tous les mois avec un montant fixé (y compris les mois futurs)
        montants.forEach(montant => {
          const key = `${montant.annee}-${montant.mois}`;
          if (!moisUniques.has(key)) {
            moisUniques.set(key, {
              annee: montant.annee,
              mois: montant.mois,
              cotisations: []
            });
          }
        });
      }
      
      // Filtrer les mois pour ne garder que ceux à partir de la date d'adhésion
      // Mais inclure tous les mois futurs avec un montant fixé
      let moisList = Array.from(moisUniques.values());
      
      if (membreData.date_adhesion) {
        const dateAdhesion = new Date(membreData.date_adhesion);
        const anneeAdhesion = dateAdhesion.getFullYear();
        const moisAdhesion = dateAdhesion.getMonth() + 1;
        
        moisList = moisList.filter(moisData => {
          // Inclure tous les mois à partir de la date d'adhésion, même les mois futurs
          return moisData.annee > anneeAdhesion || 
                 (moisData.annee === anneeAdhesion && moisData.mois >= moisAdhesion);
        });
      }
      
      // Trier (plus récent en premier)
      moisList.sort((a, b) => {
        if (a.annee !== b.annee) return b.annee - a.annee;
        return b.mois - a.mois;
      });
      
      console.log('📅 Mois uniques trouvés:', moisList.length);
      setTousLesMois(moisList);
      
      // Grouper les cotisations par mois pour l'affichage
      const cotisationsGrouped = {};
      cotisations.forEach(cotisation => {
        const key = `${cotisation.annee}-${cotisation.mois}`;
        if (!cotisationsGrouped[key]) {
          cotisationsGrouped[key] = [];
        }
        cotisationsGrouped[key].push(cotisation);
      });
      setCotisationsParMois(cotisationsGrouped);
      
      console.log('✅ Données chargées avec succès');
    } catch (err) {
      console.error('❌ Erreur lors du chargement des données:', err);
      console.error('❌ Détails:', err.response?.data || err.message);
      notificationService.error('Erreur', 'Erreur lors du chargement des données');
    } finally {
      setLoading(false);
    }
  }, [membreId, navigate]);

  useEffect(() => {
    if (!finances.id) {
      navigate('/login-finances');
      return;
    }
    if (finances.role !== 'finances') {
      navigate('/login-finances');
      return;
    }
    
    // Réinitialiser les états liés aux cotisations quand le membre change
    setCotisationsParMois({});
    setMoisExpande({});
    setLoadingCotisationsParMois({});
    setTousLesMois([]);
    
    loadData();
  }, [finances.id, finances.role, navigate, membreId, loadData]);

  const getMoisNom = (mois) => {
    const moisNoms = [
      'Janvier', 'Février', 'Mars', 'Avril', 'Mai', 'Juin',
      'Juillet', 'Août', 'Septembre', 'Octobre', 'Novembre', 'Décembre'
    ];
    return moisNoms[mois - 1] || mois;
  };

  const loadCotisationsImpayeesParMois = async (mois, annee) => {
    if (!membreId) return;
    
    const key = `${annee}-${mois}`;
    setLoadingCotisationsParMois(prev => ({ ...prev, [key]: true }));
    try {
      const response = await getCotisationsImpayeesByMois(mois, annee, parseInt(membreId));
      // Filtrer uniquement les cotisations impayées
      const cotisationsImpayees = (response.data || []).filter(c => c.statut === 'impaye');
      setCotisationsParMois(prev => ({ ...prev, [key]: cotisationsImpayees }));
    } catch (err) {
      console.error(`Erreur lors du chargement des cotisations pour ${mois}/${annee}:`, err);
      setCotisationsParMois(prev => ({ ...prev, [key]: [] }));
    } finally {
      setLoadingCotisationsParMois(prev => ({ ...prev, [key]: false }));
    }
  };

  const toggleMoisExpande = (mois, annee) => {
    const key = `${annee}-${mois}`;
    const isExpanded = moisExpande[key] || false;
    
    if (!isExpanded) {
      if (!cotisationsParMois[key]) {
        loadCotisationsImpayeesParMois(mois, annee);
      }
    }
    
    setMoisExpande(prev => ({ ...prev, [key]: !isExpanded }));
  };

  const handleConfirmerPaiement = async (cotisation) => {
    if (cotisation.statut === 'paye') {
      notificationService.warning('Déjà payée', 'Cette cotisation est déjà payée.');
      return;
    }

    const membreIdNum = parseInt(membreId);
    if (!membreIdNum) {
      notificationService.error('Erreur', 'Impossible d\'identifier le membre.');
      return;
    }

    if (cotisation.membre_id && cotisation.membre_id !== membreIdNum) {
      notificationService.error('Erreur', 'Cette cotisation n\'appartient pas au membre sélectionné.');
      return;
    }

    const montant = parseFloat(cotisation.montant || 0);
    if (!montant) {
      notificationService.error('Erreur', 'Aucun montant défini pour cette cotisation');
      return;
    }

    const membreNom = cotisation.membre ? `${cotisation.membre.nom} ${cotisation.membre.prenom}` : (membre ? `${membre.nom} ${membre.prenom}` : 'Membre');
    const messageConfirmation = `Confirmer le paiement de ${montant.toLocaleString('fr-FR', { minimumFractionDigits: 0, maximumFractionDigits: 0 })} CDF pour ${membreNom} - Semaine ${cotisation.semaine} de ${getMoisNom(cotisation.mois)} ${cotisation.annee} ?`;

    if (!window.confirm(messageConfirmation)) {
      return;
    }

    try {
      let cotisationId = cotisation.id;
      
      // Si la cotisation n'existe pas encore (pas d'ID), la créer d'abord
      if (!cotisationId) {
        const montantMensuel = montantsMensuels.find(m => m.mois === cotisation.mois && m.annee === cotisation.annee);
        if (!montantMensuel) {
          notificationService.error('Erreur', 'Aucun montant mensuel trouvé pour ce mois.');
          return;
        }
        
        try {
          // Créer la cotisation
          const cotisationResponse = await createCotisation({
            membre_id: membreIdNum,
            mois: cotisation.mois,
            annee: cotisation.annee,
            semaine: cotisation.semaine,
            montant: montant,
            date_echeance: cotisation.date_echeance
          });
          
          console.log('✅ Réponse création cotisation:', cotisationResponse.data);
          
          // Vérifier la structure de la réponse
          if (cotisationResponse.data && cotisationResponse.data.cotisation) {
            cotisationId = cotisationResponse.data.cotisation.id;
          } else if (cotisationResponse.data && cotisationResponse.data.id) {
            // Si la réponse est directement la cotisation
            cotisationId = cotisationResponse.data.id;
          } else {
            throw new Error('Structure de réponse inattendue lors de la création de la cotisation');
          }
        } catch (cotisationError) {
          console.error('❌ Erreur lors de la création de la cotisation:', cotisationError);
          const cotisationErrorMessage = cotisationError.response?.data?.message || cotisationError.message || 'Erreur lors de la création de la cotisation';
          notificationService.error('Erreur', cotisationErrorMessage);
          return;
        }
      }

      console.log('💰 Création du paiement pour cotisation ID:', cotisationId);
      
      const paiementData = {
        membre_id: membreIdNum,
        montant: montant,
        date_paiement: new Date().toISOString().split('T')[0],
        type_paiement: 'cotisation_mensuelle',
        cotisation_id: cotisationId,
        confirmed_by: 'finances'
      };

      // Mode en ligne : enregistrer normalement
      try {
        const paiementResponse = await createPaiement(paiementData);

        console.log('✅ Paiement créé avec succès:', paiementResponse.data);
        notificationService.success('Paiement confirmé', `Le paiement de ${membreNom} a été confirmé avec succès.`);
        
        // Recharger les cotisations pour ce mois
        await loadCotisationsImpayeesParMois(cotisation.mois, cotisation.annee);
        // Recharger les données du membre
        await loadData();
      } catch (paiementError) {
        console.error('❌ Erreur lors de la création du paiement:', paiementError);
        const paiementErrorMessage = paiementError.response?.data?.message || paiementError.response?.data?.error || paiementError.message || 'Erreur lors de la création du paiement';
        notificationService.error('Erreur', paiementErrorMessage);
      }
    } catch (err) {
      console.error('❌ Erreur générale lors de la confirmation du paiement:', err);
      const errorMessage = err.response?.data?.message || err.response?.data?.error || err.message || 'Erreur lors de la confirmation du paiement';
      notificationService.error('Erreur', errorMessage);
    }
  };

  if (loading) {
    return (
      <div className="loading">
        <div className="spinner"></div>
        <p>Chargement...</p>
      </div>
    );
  }

  if (!membre) {
    return (
      <div className="container">
        <div className="card">
          <h2>Membre non trouvé</h2>
          <button className="btn btn-secondary" onClick={() => navigate('/finances')}>
            Retour à la liste
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="App">
      <div className="header">
        <div className="header-content">
          <div style={{ display: 'flex', alignItems: 'center', gap: '15px' }}>
            <img src="/logo.png" alt="Logo" style={{ height: '50px', width: '50px', objectFit: 'contain' }} />
            <div>
              <h1>MUTU NDE ESPOIR ASBL</h1>
              <p>Confirmation des paiements - Finances</p>
              <p style={{ fontSize: '14px', color: '#666' }}>
                {membre.nom} {membre.postnom || ''} {membre.prenom} - Matricule: {membre.numero_matricule}
              </p>
            </div>
          </div>
          <div className="header-actions">
            <button className="btn btn-secondary" onClick={() => navigate('/finances')}>
              ← Retour à la liste
            </button>
            <button className="btn btn-danger" onClick={() => {
              localStorage.removeItem('finances_token');
              localStorage.removeItem('finances_user');
              navigate('/login-finances');
            }}>
              Déconnexion
            </button>
          </div>
        </div>
      </div>

      <div className="container">
        {/* Carte d'information du membre */}
        <div className="card" style={{ 
          marginBottom: '30px', 
          backgroundColor: '#e3f2fd', 
          border: '2px solid #2196f3',
          borderRadius: '10px',
          padding: '20px'
        }}>
          <h2 style={{ 
            marginBottom: '15px', 
            color: '#1565c0',
            fontSize: '22px',
            display: 'flex',
            alignItems: 'center',
            gap: '10px'
          }}>
            👤 Informations du membre
          </h2>
          <div style={{ 
            display: 'grid', 
            gridTemplateColumns: 'repeat(auto-fit, minmax(250px, 1fr))', 
            gap: '15px' 
          }}>
            <div style={{
              padding: '15px',
              backgroundColor: '#fff',
              borderRadius: '8px',
              border: '1px solid #90caf9'
            }}>
              <div style={{ fontSize: '12px', color: '#1565c0', marginBottom: '5px', fontWeight: 'bold' }}>NOM COMPLET</div>
              <div style={{ fontSize: '18px', color: '#1565c0', fontWeight: 'bold', whiteSpace: 'nowrap' }}>
                {membre.nom} {membre.postnom || ''} {membre.prenom}
              </div>
            </div>
            <div style={{
              padding: '15px',
              backgroundColor: '#fff',
              borderRadius: '8px',
              border: '1px solid #90caf9'
            }}>
              <div style={{ fontSize: '12px', color: '#1565c0', marginBottom: '5px', fontWeight: 'bold' }}>NUMÉRO MATRICULE</div>
              <div style={{ fontSize: '18px', color: '#1565c0', fontWeight: 'bold', whiteSpace: 'nowrap' }}>
                {membre.numero_matricule || '-'}
              </div>
            </div>
            {membre.association && (
              <div style={{
                padding: '15px',
                backgroundColor: '#fff',
                borderRadius: '8px',
                border: '1px solid #90caf9'
              }}>
                <div style={{ fontSize: '12px', color: '#1565c0', marginBottom: '5px', fontWeight: 'bold' }}>ASSOCIATION</div>
                <div style={{ fontSize: '16px', color: '#1565c0', whiteSpace: 'nowrap' }}>
                  {membre.association}
                </div>
              </div>
            )}
            {membre.centre_sante && (
              <div style={{
                padding: '15px',
                backgroundColor: '#fff',
                borderRadius: '8px',
                border: '1px solid #90caf9'
              }}>
                <div style={{ fontSize: '12px', color: '#1565c0', marginBottom: '5px', fontWeight: 'bold' }}>CENTRE DE SANTÉ</div>
                <div style={{ fontSize: '16px', color: '#1565c0', whiteSpace: 'nowrap' }}>
                  {membre.centre_sante}
                </div>
              </div>
            )}
          </div>
        </div>

        <div className="card" style={{ marginBottom: '30px' }}>
          <h2 style={{ marginBottom: '20px' }}>📋 Confirmation des paiements par mois</h2>
          <p style={{ color: '#666', marginBottom: '15px', fontSize: '14px' }}>
            Confirmez les paiements hebdomadaires pour chaque mois. Les cotisations sont organisées par semaine (1 à 4).
          </p>
          
          {/* Légende des couleurs */}
          <div style={{ 
            display: 'flex', 
            flexWrap: 'wrap', 
            gap: '15px', 
            marginBottom: '20px',
            padding: '15px',
            backgroundColor: '#f8f9fa',
            borderRadius: '8px',
            border: '1px solid #e0e0e0'
          }}>
            <span style={{ fontWeight: 'bold', color: '#333', marginRight: '10px' }}>Légende :</span>
            <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
              <div style={{ 
                width: '20px', 
                height: '20px', 
                backgroundColor: '#d4edda', 
                border: '2px solid #28a745',
                borderRadius: '4px' 
              }}></div>
              <span style={{ fontSize: '13px', color: '#155724' }}>Mois entièrement payé</span>
            </div>
            <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
              <div style={{ 
                width: '20px', 
                height: '20px', 
                backgroundColor: '#fff3cd', 
                border: '2px solid #ffc107',
                borderRadius: '4px' 
              }}></div>
              <span style={{ fontSize: '13px', color: '#856404' }}>Cotisation(s) impayée(s)</span>
            </div>
            <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
              <div style={{ 
                width: '20px', 
                height: '20px', 
                backgroundColor: '#f8d7da', 
                border: '2px solid #dc3545',
                borderRadius: '4px' 
              }}></div>
              <span style={{ fontSize: '13px', color: '#721c24' }}>Mois non cotisé</span>
            </div>
          </div>
          
          {tousLesMois.length === 0 ? (
            <div style={{ textAlign: 'center', padding: '40px', color: '#666' }}>
              <p style={{ fontSize: '16px', marginBottom: '10px' }}>Aucune cotisation trouvée</p>
              <p style={{ fontSize: '14px' }}>Ce membre n'a pas encore de cotisations enregistrées.</p>
            </div>
          ) : (
            <div style={{ display: 'flex', flexDirection: 'column', gap: '15px' }}>
              {tousLesMois.map((moisData) => {
                  const key = `${moisData.annee}-${moisData.mois}`;
                  const isExpanded = moisExpande[key] || false;
                  
                  // Obtenir les cotisations pour ce mois (impayées uniquement)
                  // Utiliser moisData.cotisations comme source de vérité principale
                  // puis cotisationsParMois[key] comme fallback si disponible
                  const cotisationsSource = moisData.cotisations && moisData.cotisations.length > 0 
                    ? moisData.cotisations 
                    : (cotisationsParMois[key] || []);
                  const cotisationsMembre = cotisationsSource.filter(c => c.statut === 'impaye');
                  
                  const loading = loadingCotisationsParMois[key] || false;
                  const totalImpaye = cotisationsMembre.reduce((sum, c) => sum + parseFloat(c.montant || 0), 0);
                  
                  // Vérifier si des cotisations existent pour ce mois (payées ou impayées)
                  const toutesCotisationsMois = moisData.cotisations || [];
                  const cotisationsExistent = toutesCotisationsMois.length > 0;
                  const toutesPayees = cotisationsExistent && cotisationsMembre.length === 0;
                  
                  // Trouver le montant mensuel fixé pour ce mois (si disponible)
                  const montantMensuel = montantsMensuels.find(m => m.mois === moisData.mois && m.annee === moisData.annee);
                  
                  // Vérifier si c'est un mois futur
                  const dateActuelle = new Date();
                  const anneeActuelle = dateActuelle.getFullYear();
                  const moisActuel = dateActuelle.getMonth() + 1;
                  const estMoisFutur = moisData.annee > anneeActuelle || 
                                      (moisData.annee === anneeActuelle && moisData.mois > moisActuel);
                  
                  // Déterminer le style selon le statut
                  const getStatusStyle = () => {
                    if (toutesPayees) {
                      // Mois entièrement payé - VERT
                      return {
                        backgroundColor: '#d4edda',
                        borderColor: '#28a745',
                        hoverBg: '#c3e6cb',
                        icon: '✅',
                        statusText: 'Payé'
                      };
                    } else if (cotisationsMembre.length > 0) {
                      // Mois avec cotisations impayées - JAUNE/ORANGE
                      return {
                        backgroundColor: '#fff3cd',
                        borderColor: '#ffc107',
                        hoverBg: '#ffe69c',
                        icon: '⚠️',
                        statusText: 'Impayé'
                      };
                    } else {
                      // Mois non cotisé (pas de cotisations créées) - ROUGE CLAIR
                      return {
                        backgroundColor: '#f8d7da',
                        borderColor: '#dc3545',
                        hoverBg: '#f5c6cb',
                        icon: '❌',
                        statusText: 'Non cotisé'
                      };
                    }
                  };
                  
                  const statusStyle = getStatusStyle();
                  
                  return (
                    <div
                      key={key}
                      style={{
                        border: `2px solid ${statusStyle.borderColor}`,
                        borderRadius: '8px',
                        overflow: 'hidden',
                        backgroundColor: '#fff',
                        boxShadow: '0 2px 4px rgba(0,0,0,0.1)'
                      }}
                    >
                      <div
                        style={{
                          padding: '15px',
                          backgroundColor: statusStyle.backgroundColor,
                          cursor: 'pointer',
                          display: 'flex',
                          justifyContent: 'space-between',
                          alignItems: 'center',
                          transition: 'background-color 0.2s'
                        }}
                        onClick={() => toggleMoisExpande(moisData.mois, moisData.annee)}
                        onMouseEnter={(e) => e.currentTarget.style.backgroundColor = statusStyle.hoverBg}
                        onMouseLeave={(e) => e.currentTarget.style.backgroundColor = statusStyle.backgroundColor}
                      >
                        <div style={{ flex: 1 }}>
                          <h3 style={{ margin: 0, fontSize: '18px', color: '#2c3e50', display: 'flex', alignItems: 'center', gap: '10px' }}>
                            <span style={{ fontSize: '20px' }}>{statusStyle.icon}</span>
                            {getMoisNom(moisData.mois)} {moisData.annee}
                            <span style={{ 
                              fontSize: '11px', 
                              padding: '4px 10px', 
                              backgroundColor: statusStyle.borderColor,
                              color: 'white', 
                              borderRadius: '12px',
                              fontWeight: 'bold',
                              textTransform: 'uppercase'
                            }}>
                              {statusStyle.statusText}
                            </span>
                            {estMoisFutur && (
                              <span style={{ 
                                fontSize: '12px', 
                                padding: '4px 8px', 
                                backgroundColor: '#e3f2fd', 
                                color: '#1976d2', 
                                borderRadius: '4px',
                                fontWeight: 'normal'
                              }}>
                                📅 Mois futur
                              </span>
                            )}
                          </h3>
                          {montantMensuel ? (
                            <div style={{ fontSize: '14px', color: '#666', marginTop: '5px' }}>
                              Montant mensuel: <strong>{parseFloat(montantMensuel.montant_mensuel).toLocaleString('fr-FR', { minimumFractionDigits: 0, maximumFractionDigits: 0 })} CDF</strong>
                              {' • '}
                              Montant hebdomadaire: <strong>{parseFloat(montantMensuel.montant_hebdomadaire).toLocaleString('fr-FR', { minimumFractionDigits: 0, maximumFractionDigits: 0 })} CDF</strong>
                            </div>
                          ) : (
                            <div style={{ fontSize: '14px', color: '#999', marginTop: '5px', fontStyle: 'italic' }}>
                              Montant mensuel non fixé par l'administrateur
                            </div>
                          )}
                          {isExpanded && (
                            <div style={{ fontSize: '13px', color: '#666', marginTop: '8px' }}>
                              {cotisationsMembre.length > 0 ? (
                                <span style={{ color: '#856404', fontWeight: 'bold' }}>
                                  {cotisationsMembre.length} cotisation(s) impayée(s) • Total: {totalImpaye.toLocaleString('fr-FR', { minimumFractionDigits: 0, maximumFractionDigits: 0 })} CDF
                                </span>
                              ) : toutesPayees ? (
                                <span style={{ color: '#155724', fontWeight: 'bold' }}>
                                  ✓ Toutes les cotisations sont payées
                                </span>
                              ) : (
                                <span style={{ color: '#1565c0', fontWeight: 'bold' }}>
                                  📋 Cotisations non encore créées
                                </span>
                              )}
                            </div>
                          )}
                        </div>
                        <div style={{ fontSize: '20px', color: '#666' }}>
                          {isExpanded ? '▼' : '▶'}
                        </div>
                      </div>
                      
                      {isExpanded && (
                        <div style={{ padding: '15px', backgroundColor: '#f8f9fa' }}>
                          {loading ? (
                            <div style={{ textAlign: 'center', padding: '20px', color: '#666' }}>
                              Chargement...
                            </div>
                          ) : (() => {
                            // Obtenir toutes les cotisations du mois (payées et impayées) pour affichage complet
                            const toutesCotisations = moisData.cotisations || [];
                            const cotisationsTriees = toutesCotisations.sort((a, b) => (a.semaine || 0) - (b.semaine || 0));
                            
                            // Trouver le montant mensuel pour ce mois
                            const montantMensuel = montantsMensuels.find(m => m.mois === moisData.mois && m.annee === moisData.annee);
                            const montantHebdomadaire = montantMensuel ? parseFloat(montantMensuel.montant_hebdomadaire) : 0;
                            
                            // Si pas de cotisations mais qu'il y a un montant mensuel, afficher les semaines appropriées
                            if (cotisationsTriees.length === 0 && montantMensuel) {
                              // Afficher toutes les semaines du mois (1, 2, 3, 4)
                              // Même si le membre adhère au dernier jour du mois, il peut payer toutes les semaines
                              let semainesAAfficher = [1, 2, 3, 4];
                              
                              // Créer un tableau des semaines appropriées
                              const semaines = semainesAAfficher.map(semaine => {
                                // Calculer la date d'échéance
                                const jourEcheance = Math.min(semaine * 7, 28);
                                const dateEcheance = new Date(moisData.annee, moisData.mois - 1, jourEcheance);
                                
                                return {
                                  semaine,
                                  mois: moisData.mois,
                                  annee: moisData.annee,
                                  montant: montantHebdomadaire,
                                  date_echeance: dateEcheance.toISOString().split('T')[0],
                                  statut: 'impaye',
                                  id: null, // Pas encore créée
                                  membre_id: parseInt(membreId)
                                };
                              });
                              
                              return (
                                <div>
                                  <div style={{ textAlign: 'center', padding: '15px', color: '#856404', backgroundColor: '#fff3cd', borderRadius: '5px', marginBottom: '15px' }}>
                                    <strong>⚠️ Les cotisations pour ce mois n'ont pas encore été créées.</strong>
                                    <div style={{ fontSize: '13px', marginTop: '5px' }}>
                                      Vous pouvez confirmer les paiements directement. Les cotisations seront créées automatiquement.
                                    </div>
                                  </div>
                                  <div style={{ overflowX: 'auto' }}>
                                    <table className="table" style={{ margin: 0 }}>
                                      <thead>
                                        <tr>
                                          <th style={{ whiteSpace: 'nowrap' }}>Semaine</th>
                                          <th style={{ whiteSpace: 'nowrap' }}>Montant</th>
                                          <th style={{ whiteSpace: 'nowrap' }}>Échéance</th>
                                          <th style={{ whiteSpace: 'nowrap' }}>Statut</th>
                                          <th style={{ whiteSpace: 'nowrap', textAlign: 'center' }}>Action</th>
                                        </tr>
                                      </thead>
                                      <tbody>
                                        {semaines.map((cotisation, index) => (
                                          <tr key={`semaine-${cotisation.semaine}`} style={{ 
                                            backgroundColor: 'transparent' 
                                          }}>
                                            <td style={{ whiteSpace: 'nowrap' }}>
                                              Semaine {cotisation.semaine}
                                            </td>
                                            <td style={{ whiteSpace: 'nowrap', fontWeight: 'bold' }}>
                                              {cotisation.montant.toLocaleString('fr-FR', { minimumFractionDigits: 0, maximumFractionDigits: 0 })} CDF
                                            </td>
                                            <td style={{ whiteSpace: 'nowrap' }}>
                                              {new Date(cotisation.date_echeance).toLocaleDateString('fr-FR')}
                                            </td>
                                            <td style={{ whiteSpace: 'nowrap' }}>
                                              <span style={{ color: '#dc3545', fontWeight: 'bold' }}>● Impayée</span>
                                            </td>
                                            <td style={{ whiteSpace: 'nowrap', textAlign: 'center' }}>
                                              <button
                                                className="btn btn-success"
                                                onClick={() => handleConfirmerPaiement(cotisation)}
                                                style={{ fontSize: '13px', padding: '6px 12px' }}
                                              >
                                                ✓ Confirmer le paiement
                                              </button>
                                            </td>
                                          </tr>
                                        ))}
                                      </tbody>
                                    </table>
                                  </div>
                                </div>
                              );
                            }
                            
                            if (cotisationsTriees.length === 0) {
                              return (
                                <div style={{ textAlign: 'center', padding: '20px', color: '#666' }}>
                                  <strong>Aucune cotisation pour ce mois</strong>
                                  {!montantMensuel && (
                                    <div style={{ fontSize: '13px', marginTop: '10px', color: '#999' }}>
                                      L'administrateur n'a pas encore fixé de montant mensuel pour ce mois.
                                    </div>
                                  )}
                                </div>
                              );
                            }
                            
                            // Afficher toutes les cotisations du mois
                            // Même si le membre adhère au dernier jour du mois, il peut payer toutes les semaines du mois en cours
                            let cotisationsFiltrees = cotisationsTriees;
                            
                            const cotisationsImpayees = cotisationsFiltrees.filter(c => c.statut === 'impaye');
                            const cotisationsPayees = cotisationsFiltrees.filter(c => c.statut === 'paye');
                            
                            return (
                              <div>
                                {/* Résumé des cotisations */}
                                <div style={{ 
                                  textAlign: 'center', 
                                  padding: '10px 15px', 
                                  marginBottom: '15px',
                                  borderRadius: '5px',
                                  backgroundColor: cotisationsImpayees.length > 0 ? '#fff3cd' : '#d4edda',
                                  color: cotisationsImpayees.length > 0 ? '#856404' : '#155724'
                                }}>
                                  {cotisationsImpayees.length > 0 ? (
                                    <strong>{cotisationsImpayees.length} cotisation(s) impayée(s) • Total: {cotisationsImpayees.reduce((sum, c) => sum + parseFloat(c.montant || 0), 0).toLocaleString('fr-FR', { minimumFractionDigits: 0, maximumFractionDigits: 0 })} CDF</strong>
                                  ) : cotisationsPayees.length > 0 ? (
                                    <strong>✓ Toutes les cotisations de ce mois sont payées!</strong>
                                  ) : (
                                    <strong>Aucune cotisation pour ce mois</strong>
                                  )}
                                </div>
                                
                                <div style={{ overflowX: 'auto' }}>
                                  <table className="table" style={{ margin: 0 }}>
                                    <thead>
                                      <tr>
                                        <th style={{ whiteSpace: 'nowrap' }}>Semaine</th>
                                        <th style={{ whiteSpace: 'nowrap' }}>Montant</th>
                                        <th style={{ whiteSpace: 'nowrap' }}>Échéance</th>
                                        <th style={{ whiteSpace: 'nowrap' }}>Statut</th>
                                        <th style={{ whiteSpace: 'nowrap', textAlign: 'center' }}>Action</th>
                                      </tr>
                                    </thead>
                                    <tbody>
                                      {cotisationsFiltrees.map((cotisation) => (
                                        <tr key={cotisation.id} style={{ 
                                          backgroundColor: cotisation.statut === 'paye' ? '#d4edda' : 'transparent' 
                                        }}>
                                          <td style={{ whiteSpace: 'nowrap' }}>
                                            Semaine {cotisation.semaine || 'N/A'}
                                          </td>
                                          <td style={{ whiteSpace: 'nowrap', fontWeight: 'bold' }}>
                                            {parseFloat(cotisation.montant || 0).toLocaleString('fr-FR', { minimumFractionDigits: 0, maximumFractionDigits: 0 })} CDF
                                          </td>
                                          <td style={{ whiteSpace: 'nowrap' }}>
                                            {cotisation.date_echeance ? new Date(cotisation.date_echeance).toLocaleDateString('fr-FR') : '-'}
                                          </td>
                                          <td style={{ whiteSpace: 'nowrap' }}>
                                            {cotisation.statut === 'paye' ? (
                                              <span style={{ color: '#28a745', fontWeight: 'bold' }}>✓ Payée</span>
                                            ) : (
                                              <span style={{ color: '#dc3545', fontWeight: 'bold' }}>● Impayée</span>
                                            )}
                                          </td>
                                          <td style={{ whiteSpace: 'nowrap', textAlign: 'center' }}>
                                            {cotisation.statut === 'impaye' ? (
                                              <button
                                                className="btn btn-success"
                                                onClick={() => handleConfirmerPaiement(cotisation)}
                                                style={{ fontSize: '13px', padding: '6px 12px' }}
                                              >
                                                ✓ Confirmer le paiement
                                              </button>
                                            ) : (
                                              <span style={{ color: '#28a745', fontSize: '13px' }}>
                                                {cotisation.date_paiement ? `Payé le ${new Date(cotisation.date_paiement).toLocaleDateString('fr-FR')}` : 'Payé'}
                                              </span>
                                            )}
                                          </td>
                                        </tr>
                                      ))}
                                    </tbody>
                                  </table>
                                </div>
                              </div>
                            );
                          })()}
                        </div>
                      )}
                    </div>
                  );
                })}
            </div>
          )}
        </div>
      </div>

      <Footer />
    </div>
  );
};

export default ConfirmationPaiementsFinances;
